import React from 'react';
import { INTERFACE_TEXT } from '../../constants/interfaceText';
import type {
  FeedbackView as FeedbackModel,
  TurnKind,
} from '../../turncard/viewModel';
import styles from './FeedbackView.module.css';

interface FeedbackViewProps {
  feedback: FeedbackModel | null;
  kind: TurnKind;
  /** The step's own text, shown when the attempt had no feedback (nothing heard). */
  fallbackText?: string;
}

/**
 * What came of the learner's attempt: the feedback line and, when the engine
 * gave one, each word marked clear or unclear (BR-026). Unclear words are
 * underlined and announced, so colour is never the only cue.
 */
export const FeedbackView: React.FC<FeedbackViewProps> = ({
  feedback,
  kind,
  fallbackText,
}) => {
  if (kind === 'nospeech') {
    return (
      <div
        className={`${styles.fb} ${styles.warn}`}
        data-testid="turncard-feedback"
      >
        {fallbackText && <p>{fallbackText}</p>}
        <p>{INTERFACE_TEXT.tcNoSpeechInvite}</p>
      </div>
    );
  }
  if (!feedback) return null;
  const tone = feedback.tone === 'ok' ? styles.ok : styles.warn;
  return (
    <div className={`${styles.fb} ${tone}`} data-testid="turncard-feedback">
      {feedback.words.length > 0 && (
        <p className={styles.words} data-testid="turncard-words">
          {feedback.words.map((w, i) => (
            <span
              key={`${i}-${w.word}`}
              className={w.clear ? styles.clear : styles.unclear}
              data-clear={w.clear ? 'true' : 'false'}
            >
              {w.word}
              {!w.clear && (
                <span className={styles.srOnly}>
                  {` (${INTERFACE_TEXT.tcUnclearWord})`}
                </span>
              )}
            </span>
          ))}
        </p>
      )}
      {feedback.text && <p>{feedback.text}</p>}
      {kind === 'retry' && <p>{INTERFACE_TEXT.tcRetryInvite}</p>}
    </div>
  );
};

export default FeedbackView;
